import type { ReactNode } from "react"
import { useEffect, useRef } from "react"
import { useHaptic } from "@/shared/hooks/use-haptic"

interface MediaThumbnailSliderItem {
	id: string
	thumbnail: ReactNode
}

interface MediaThumbnailSliderProps {
	items: MediaThumbnailSliderItem[]
	selectedId: string
	onSelect: (id: string) => void
	className?: string
}

export function MediaThumbnailSlider({
	items,
	selectedId,
	onSelect,
	className = "mt-3 mb-4",
}: MediaThumbnailSliderProps) {
	const { triggerSelection } = useHaptic()
	const containerRef = useRef<HTMLDivElement>(null)
	const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({})

	useEffect(() => {
		if (items.length < 2) return
		const el = buttonRefs.current[selectedId]
		el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
	}, [selectedId, items.length])

	if (items.length < 2) return null

	return (
		<div className={`relative ${className}`}>
			<div ref={containerRef} className="hide-scrollbar -mx-1.5 overflow-x-auto px-1.5 py-2">
				<div className="flex gap-3 pr-8 pb-1">
					{items.map((item) => (
						<button
							key={item.id}
							type="button"
							ref={(el) => {
								buttonRefs.current[item.id] = el
							}}
							className={`w-44 shrink-0 rounded-xl p-0.5 ring-offset-background transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none ${
								item.id === selectedId ? "ring-2 ring-primary ring-offset-2" : "ring-1 ring-border"
							}`}
							onClick={() => {
								triggerSelection()
								onSelect(item.id)
							}}
						>
							{item.thumbnail}
						</button>
					))}
				</div>
			</div>
			<div className="pointer-events-none absolute top-0 right-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent" />
		</div>
	)
}
